var Component = require("enyo/Component"),
    config    = require("app/config"),
    get       = require("object-path").get,
    kind      = require("enyo/kind"),
    logger    = require("enyo/logger"),
    tag       = require("app/ConsentManagement/tag.js");

module.exports = kind(/** @lends  app.ConsentManagement.PrivacyService.prototype */ {

    /**
     * @private
     */
    name: "app.ConsentManagement.PrivacyService",

    /**
     * @private
     */
    kind: Component,

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.onCmpUIMutation = this.bindSafely(this.onCmpUIMutation);
            sup.apply(this, arguments);

            // Consent data state
            this.set("iabPromise", new Promise(this.bindSafely(function(resolve) {
                this.iabResolve = resolve;
            })));
            this.set("nonIABPromise", new Promise(this.bindSafely(function(resolve) {
                this.nonIABResolve = resolve;
            })));
        };
    }),

    /**
     * @public
     */
    start: function() {
        // IOS App might come with a flag to disallow tracking
        if (typeof __Vikaru !== "undefined" && !__Vikaru.allowTracking) {
            this.disableTracking();

            return;
        }

        // The cookie with consent does not depend on the CMP to be loaded. We
        // can attempt to load it immediately. If it's present, it'll be loaded
        // pretty much synchronously.
        if (!this.shouldBypassConsent()) {
            this.getCookieYesConsentData();
        }
        else {
            this.acceptNonIABConsentData(null);
        }

        // Load the tag
        this.initTag();

        // Only do TCF API stuff if IAB is enabled
        if (this.isIabEnabled()) {
            // Wait for TCF API to be ready
            this.pingTcfApi();

            // TCF consent data relies on the TCF API to be ready. If it's present,
            // the TCF API will broadcast it to us asynchronously.
            if (!this.shouldBypassConsent()) {
                this.getTCFConsentData();
            }
            else {
                this.acceptIABConsentData(null);
            }
        }
    },

    /**
     * Alternative start-up sequence for environments where tracking is
     * disallowed.
     *
     * @private
     */
    disableTracking: function() {
        // No CMP
        this.set("tagLoaded", false);
        logger.log("CMP Tag loading canceled because tracking is not allowed");
        this.set("tagLoadedPromise", Promise.resolve(false));

        // No TCF API
        this.set("tcfApiReady", false);
        logger.log("TCF API not in use because because tracking is not allowed");
        this.set("tcfApiReadyPromise", Promise.resolve(false));

        // No IAB Consent data
        if (this.isIabEnabled()) {
            this.set("iabConsentData", null);
            this.iabResolve();
        }

        // No Non-IAB Consent data
        this.set("nonIABConsentData", null);
        this.nonIABResolve();
    },

    /**
     * Check for flag telling to bypass consent checks.
     *
     * @return {boolean} Whether the consent check bypass flag was set
     * @public
     */
    shouldBypassConsent: function() {
        return get(config, "cookieYes.bypassConsent", false)
         || new URLSearchParams(window.location.search).get("bypass_consent") === "1";
    },

    /**
     * Verify if IAB is enabled.
     *
     * @return {boolean} Whether IAB is enabled.
     * @public
     */
    isIabEnabled: function() {
        return get(config, "cookieYes.iabEnabled", false);
    },

    // Tag Readiness
    // --------------------------------

    /**
     * Whether the CMP tag is loaded.
     *
     * @type  {boolean}
     * @private
     */
    tagLoaded: false,

    /**
     * A promise that resolves when the CMP tag has loaded.
     *
     * @type {Promise}
     * @private
     */
    tagLoadedPromise: null,

    /**
     * Set up event listeners and execute the tag.
     *
     * @private
     */
    initTag: function() {
        var onTagLoaded,
            onTagError,
            resolver,
            allowedVendors;

        // No consent checks
        if (this.shouldBypassConsent()) {
            this.set("tagLoaded", false);
            logger.log("CMP Tag loading canceled because consent requirement was bypassed");
            this.set("tagLoadedPromise", Promise.resolve(false));
        }

        // Tag has already been initialized
        if (this.tagLoadedPromise) {
            return;
        }

        this.set("tagLoadedPromise", new Promise(function(resolve) {
            resolver = resolve;
        }));

        // Set listeners for tag readiness
        onTagLoaded = this.bindSafely(function() {
            this.set("tagLoaded", true);
            resolver(true);
            document.removeEventListener("cookieyes_tag_loaded", onTagLoaded);
            this.monitorCmpUI();
        });
        document.addEventListener("cookieyes_tag_loaded", onTagLoaded);

        onTagError  = this.bindSafely(function(event) {
            this.set("tagLoaded", false);
            logger.log("CMP Tag not loaded: " + event.detail);
            resolver(false);
            document.removeEventListener("cookieyes_tag_error", onTagError);
        });
        document.addEventListener("cookieyes_tag_error", onTagError);

        // Set listener for consent data from the CMP. We do this because the
        // CMP might fire it immediately once it's been initialized
        document.addEventListener("cookieyes_consent_update", this.bindSafely(function(event) {
            this.acceptNonIABConsentData(event.detail);
        }));

        // Whitelist IAB Vendors
        allowedVendors   = get(config, "adAlliance.iabVendors", null);

        if (allowedVendors) {
            window.iabConfig = {
                allowedVendors: allowedVendors
            };
        }

        // Execute the tag
        tag(config);
    },

    // TCF API Readiness
    // --------------------------------

    /**
     * Whether the TCF API is ready.
     *
     * @type  {boolean}
     * @private
     */
    tcfApiReady: false,

    /**
     * A promise that resolves when the TCF API is ready.
     *
     * @type {Promise}
     * @private
     */
    tcfApiReadyPromise: null,

    /**
     * Listen for readiness of the TCF API.
     *
     * @private
     */
    pingTcfApi: function() {
        var resolver;

        // No consent checks
        if (this.shouldBypassConsent()) {
            this.set("tcfApiReady", false);
            logger.log("TCF API not in use because consent requirement was bypassed");
            this.set("tcfApiReadyPromise", Promise.resolve(false));
        }

        // IAB disabled
        if (!this.isIabEnabled()) {
            this.set("tcfApiReady", false);
            logger.log("TCF API not in use because IAB is not enabled");
            this.set("tcfApiReadyPromise", Promise.resolve(false));
        }

        // Tag has already been initialized
        if (this.tcfApiReadyPromise) {
            return;
        }

        this.set("tcfApiReadyPromise", new Promise(function(resolve) {
            resolver = resolve;
        }));

        this.tagLoadedPromise.then(this.bindSafely(function() {
            var that    = this,
                ping,
                start   = Date.now() / 1000,
                timeout = get(config, "cookieYes.cmpTimeout", 10);

            ping = function() {
                if (window.hasOwnProperty("__tcfapi")) {
                    __tcfapi("ping", 2, function(pingReturn) {
                        if (pingReturn.cmpLoaded) {
                            that.set("tcfApiReady", true);
                            resolver(true);
                        }
                        else if (Date.now() / 1000 < start + timeout) {
                            setTimeout(function() {
                                ping();
                            }, 1000);
                        }
                        else {
                            that.set("tcfApiReady", false);

                            logger.log("__tcfapi ping timeout");

                            resolver(false);
                        }
                    });
                }
                else {
                    that.set("tcfApiReady", false);

                    logger.log("__tcfapi unavailable");

                    resolver(false);
                }
            };

            ping();
        }));
    },

    // CMP UI Visibility
    // --------------------------------

    /**
     * @type {boolean}
     * @readonly
     * @public
     */
    cmpUiShown: false,

    /**
     * @private
     */
    cmpOverlayElement: null,

    /**
     * @private
     */
    cmpConsentContainer: null,

    /**
     * @private
     */
    cmpModal: null,

    /**
     * @private
     */
    cmpUIMutationObserver: null,

    /**
     * @private
     */
    monitorCmpUI: function() {
        var awaitCmp;

        awaitCmp = this.bindSafely(function() {
            this.cmpUIMutationObserver = new MutationObserver(this.onCmpUIMutation);
            this.cmpOverlayElement     = document.querySelector("body > .cky-overlay");
            this.cmpConsentContainer   = document.querySelector("body > .cky-consent-container");
            this.cmpModal              = document.querySelector("body > .cky-modal");

            if (this.cmpOverlayElement && this.cmpConsentContainer) {
                this.cmpUIMutationObserver.observe(this.cmpOverlayElement, {
                    attributes: true,
                    childList:  false,
                    subtree:    false
                });
                this.cmpUIMutationObserver.observe(this.cmpConsentContainer, {
                    attributes: true,
                    childList:  false,
                    subtree:    false
                });
                this.cmpUIMutationObserver.observe(this.cmpModal, {
                    attributes: true,
                    childList:  false,
                    subtree:    false
                });
                this.onCmpUIMutation();
            }
            else {
                setTimeout(function() {
                    awaitCmp();
                }, 100);
            }
        });

        awaitCmp();
    },

    /**
     * @private
     */
    onCmpUIMutation: function() {
        var cmpUiShown;

        cmpUiShown = !this.cmpOverlayElement.classList.contains("cky-hide")
            || !this.cmpConsentContainer.classList.contains("cky-hide")
            || this.cmpModal.classList.contains("cky-modal-open");

        this.set("cmpUiShown", cmpUiShown);
    },

    // TCF Consent data retrieval
    // --------------------------------

    /**
     * Transparency and Consent data.
     *
     * @type {?TCData}
     * @readonly
     * @public
     */
    iabConsentData: null,

    /**
     * Promise for loading iabConsentData.
     *
     * @type {?Promise}
     * @readonly
     * @public
     */
    iabPromise: null,

    /**
     * Promise resolver for `iabPromise`.
     *
     * @type {?function}
     * @private
     */
    iabResolve: null,

    /**
     * @private
     */
    getTCFConsentData: function() {
        Promise.all([this.tagLoadedPromise, this.tcfApiReadyPromise])
            .then(this.bindSafely(function(responses) {
                var tcfApiReady = responses[1],
                    errorMsg;

                if (tcfApiReady) {
                    __tcfapi("addEventListener", 2, this.bindSafely(function(tcData, success) {
                        if (success) {
                            if (["tcloaded", "useractioncomplete"].includes(tcData.eventStatus)) {
                                this.acceptIABConsentData(tcData);
                            }
                        }
                        else {
                            errorMsg = "__tcfapi EventListener failed";
                            logger.error(errorMsg);

                            if (typeof Sentry !== "undefined") {
                                Sentry.captureException(new Error(errorMsg));
                            }

                            this.acceptIABConsentData(null);
                        }
                    }));
                }
                else {
                    errorMsg = "__tcfapi unavailable";
                    logger.error(errorMsg);

                    this.acceptIABConsentData(null);
                }
            }));
    },

    /**
     * @param  {Object|null}  data  The IAB Consent Data
     * @private
     */
    acceptIABConsentData: function(data) {
        this.set("iabConsentData", data);
        this.iabResolve();
    },

    // CookieYes Consent data retrieval
    // --------------------------------

    /**
     * Non IAB Consent data.
     *
     * @type {?Object}
     * @readonly
     * @public
     */
    nonIABConsentData: null,

    /**
     * Promise for loading nonIabConsentData.
     *
     * @type {?Promise}
     * @readonly
     * @public
     */
    nonIabPromise: null,

    /**
     * Promise resolver for `nonIabPromise`.
     *
     * @type {?function}
     * @private
     */
    nonIabResolve: null,

    /**
     * CookieYes permissions
     *
     * @type {array<string>}
     * @private
     */
    cookieYesPermissions: [
        "necessary",
        "functional",
        "analytics",
        "performance",
        "advertisement",
        "other"
    ],

    /**
     * Attempt to load consent data from CookieYes consent cookie.
     *
     * @private
     */
    getCookieYesConsentData: function() {
        var cookies = document.cookie || "",
            cookie,
            properties,
            accepted,
            rejected;

        // Data already loaded
        if (this.get("nonIABConsentData")) {
            return;
        }

        cookie = cookies
            .split(";")
            .map(function(str) {
                return str.trim();
            })
            .filter(function(cookie) {
                return cookie.split("=")[0] === "cookieyes-consent";
            })[0];

        if (!cookie) {
            return;
        }

        properties = cookie.split("=")[1].split(",")
            .map(function(str) {
                return str.trim();
            });

        accepted = properties.reduce(this.bindSafely(function(accepted, property) {
            var parts = property.split(":");

            if (this.cookieYesPermissions.includes(parts[0])) {
                if (parts[1] === "yes") {
                    accepted.push(parts[0]);
                }
            }

            return accepted;
        }), []);

        rejected = this.cookieYesPermissions.reduce(this.bindSafely(function(rejected, property) {
            if (!accepted.includes(property)) {
                rejected.push(property);
            }

            return rejected;
        }), []);

        this.acceptNonIABConsentData({
            accepted: accepted,
            rejected: rejected
        });
    },

    /**
     * @param  {Object|null}  data  The IAB Consent Data
     * @private
     */
    acceptNonIABConsentData: function(data) {
        this.set("nonIABConsentData", data);
        this.nonIABResolve();
    }

});
